import { formatHours, percentOf, SERIES } from '../charts/chart-theme';
import { StackedAreaTrend } from '../charts/stacked-area-trend';
import { WaterfallChart, type WaterfallStep } from '../charts/waterfall-chart';
import { WidgetCard, type WidgetInsights } from '../widget-card';
import { WidgetLegend } from '../widget-legend';
import { CATEGORIES, FIRM, MONTHS } from './fixtures';

/** Shape of the 12-month mix, as percentage points of net-available capacity. */
const TREND_BASELINE: Record<string, number> = {
  hourly: 11,
  retainer: 42,
  free: 3,
  internal: 26,
  leave: 6,
  idle: 6,
};

const idleHours = Math.max(
  0,
  FIRM.available - FIRM.billable - FIRM.nonBillable - FIRM.free - FIRM.internal,
);

const STEPS: WaterfallStep[] = [
  { name: 'Net available', value: FIRM.available, color: SERIES.neutral, absolute: true },
  { name: 'Client billable', value: FIRM.billable, color: SERIES.billable },
  { name: 'Non-billable', value: FIRM.nonBillable, color: SERIES.nonBillable },
  { name: '$1+Free / Dead', value: FIRM.free, color: SERIES.free },
  { name: 'Internal msCFO', value: FIRM.internal, color: SERIES.internal },
  { name: 'Idle / Balance', value: idleHours, color: SERIES.idle, absolute: true },
];

/**
 * Where the firm's capacity went: net-available hours draining into each class
 * of work, with whatever is left standing as idle.
 *
 * Leave and holiday are already netted out of "available", so they don't appear
 * as steps — the waterfall starts from capacity that could have been worked.
 */
export function CapacityTile({
  insights,
  onDrill,
}: {
  insights: WidgetInsights;
  onDrill: (stepName: string) => void;
}) {
  const trendRows = MONTHS.map((_, i) => {
    const row: Record<string, number> = {};
    CATEGORIES.forEach((c, ci) => {
      const base = TREND_BASELINE[c.key] ?? 0;
      // Gentle per-series drift so the bands read as a history rather than a
      // repeated column. Replaced by real monthly aggregates with the API.
      row[c.key] = Number((base + Math.sin(i / 2 + ci) * 1.6).toFixed(1));
    });
    return row;
  });

  return (
    <WidgetCard
      title="Capacity"
      subtitle={`${formatHours(FIRM.available)} net-available hrs → how they were spent · ${percentOf(idleHours, FIRM.available)}% left idle`}
      insights={insights}
    >
      <WaterfallChart steps={STEPS} onStepClick={onDrill} />
      <div className="mt-2 border-t pt-2">
        <StackedAreaTrend
          labels={MONTHS}
          rows={trendRows}
          series={CATEGORIES.map((c) => ({ key: c.key, label: c.label, color: c.color }))}
        />
        <WidgetLegend items={CATEGORIES.map((c) => ({ color: c.color, label: c.label }))} />
      </div>
    </WidgetCard>
  );
}
